import React, { useCallback, useEffect, useState } from "react";
import { Card, Button, Table, Tag, Select, Space, message, Typography, Modal, Statistic, Row, Col, Tabs, Form, InputNumber, Tooltip, Input, Popconfirm, Steps, Drawer, Descriptions, Checkbox, Collapse, Dropdown } from "antd";
import InsuranceListModal from "../components/InsuranceListModal";
import Alert from "../components/GxAlert";
import {
  CalculatorOutlined, CheckOutlined, WarningOutlined, LockOutlined, RollbackOutlined, UnlockOutlined, ClockCircleOutlined,
  FilePdfOutlined, FileExcelOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, DeleteOutlined,
  ProfileOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import ExportButton from "../components/ExportButton";
import RecordEditorModal from "../components/RecordEditorModal";
import DebtDeferralPanel from "../components/DebtDeferralPanel";
import PersonDaysPanel from "./attendance/PersonDaysPanel";
import RecordContextPanel from "../components/RecordContextPanel";
import PayrollAdjustmentsEditor, {
  adjTotals, newAdjRow, toApiItems, toDraftRows, validateRows,
} from "../components/PayrollAdjustmentsEditor";
import api, {
  getPeriods, createPeriod, calculatePayroll, approvePayroll, getPayrollRecords, resetPayroll, deletePeriod,
  updatePayrollRecord, deletePayrollRecord,
  getWizardState, approveAccountant, approveManagement, lockPeriod, closingRevert, reopenPeriod,
  closePayGroup, reopenPayGroup,
  getPayrollPipeline, getEmployeeDossier, patchEmployeeReview, getDepartments,
  explainPayroll, putPayrollAdjustments, getPieceRates, recalcEmployeePayroll, downloadFile,
} from "../api/client";
import { EditOutlined } from "@ant-design/icons";
import { byNum, byStr } from "../utils/sorters";
import { currentJalali, fmtNum, toJalali } from "../utils/dateUtils";
import { MoneyInput, formatMoney } from "../utils/money";
import EmptyWorkArea from "../components/EmptyWorkArea";
import GxTable from "../components/GxTable";
import GxTabs from "../components/GxTabs";
import HelpPopover from "../components/HelpPopover";
import { faNum } from "../utils/faNumberFormat";
import { PayrollExplainBody } from "../components/PayrollExplainDrawer";
import usePdfPreview from "../utils/usePdfPreview";
import NewStaffTag from "../components/NewStaffTag";
import { getActiveYm, rememberPeriod } from "../utils/activeMonth";

// «روزمزد» از v2.51.0 حذف شد (با «ثابت» یکی است)؛ برچسبش فقط برای دادهٔ قدیمی مانده
const PAY_MODEL_LABEL = { piece: "کارمزدی", daily: "ثابت", fixed: "ثابت", none: "بدون پایه" };
const CLOSING_STEPS = [null, "accountant_approved", "management_approved", "locked"];
const CLOSING_LABEL = { accountant_approved: "تأیید حسابداری", management_approved: "تأیید مدیریت", locked: "قفل نهایی" };

/* آدرسِ API از کلاینتِ مشترک (client.js) — قبلاً اینجا نمونهٔ محلیِ axios با
   پورتِ ۸۰۰۰ هاردکد بود و روی HTTPS (پورتِ بسته) «خطا در بارگذاری» می‌داد. */
const { Title, Text } = Typography;
const { Option } = Select;

const MONTHS = ["فروردین","اردیبهشت","خرداد","تیر","مرداد","شهریور",
                 "مهر","آبان","آذر","دی","بهمن","اسفند"];
const YEARS = [1402, 1403, 1404, 1405, 1406];

const statusColors = { open: "default", calculated: "blue", approved: "green", paid: "gold" };
const statusLabels = { open: "باز", calculated: "محاسبه‌شده", approved: "تایید‌شده", paid: "پرداخت‌شده" };


function fmt(n) {
  return formatMoney(n);
}


export default function PayrollPage() {
  // تفکیکِ «چرا این عدد؟» — کنارِ پروندهٔ حقوقی بارگذاری می‌شود
  const [explainData, setExplainData] = useState(null);
  // پیش‌نمایشِ PDF (فیش) قبل از دانلود/چاپ — خواستهٔ کاربر
  const pdf = usePdfPreview();
  const navigate = useNavigate();
  // لینکِ «دیدن فیش‌های منفی» از پنلِ نیازمند بررسی: /payroll?period_id=..&filter=negative
  const [searchParams] = useSearchParams();
  /* v2.160.0 (کاربر): «وقتی لیستِ بیمه را می‌گیرم بپرسد کدام حساب‌ها: اصلی، سامان، بانکِ دیگر یا همه؛ پیش‌فرض هم داشته باشد» */
  const [insAsk, setInsAsk] = useState(null);           // {run} — مودالِ انتخابِ حساب (`InsuranceListModal`)
  const [onlyNegative, setOnlyNegative] = useState(
    () => searchParams.get("filter") === "negative");
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // زمینهٔ نفرِ جاری داخلِ «ویرایش رکورد حقوق» (حضور، کارمزد، بیمه)
  const [ctx, setCtx] = useState({ empId: null, loading: false, explain: null, dossier: null });

  /** با هر جابه‌جایی بینِ نفرات، جزئیاتِ همان نفر خوانده می‌شود. */
  const loadCtx = useCallback((employeeId) => {
    if (!selectedPeriod || !employeeId) { setCtx({ empId: null, loading: false, explain: null, dossier: null }); return; }
    setCtx((c) => ({ ...c, empId: employeeId, loading: true }));
    Promise.allSettled([
      explainPayroll(selectedPeriod, employeeId),
      getEmployeeDossier(selectedPeriod, employeeId),
    ]).then(([ex, dos]) => setCtx({
      empId: employeeId, loading: false,
      explain: ex.status === "fulfilled" ? ex.value.data : null,
      dossier: dos.status === "fulfilled" ? dos.value.data : null,
    }));
  }, [selectedPeriod]);
  // پیش‌نویسِ «ردیف‌های اصلاحی» نفرِ جاری در ویرایشگر — از dossier پر می‌شود و با
  // هر ذخیره/جابه‌جایی (بارگذاریِ دوبارهٔ dossier) از نو ساخته می‌شود.
  const [adj, setAdj] = useState({ empId: null, rows: [], saved: { earn: 0, deduct: 0 }, dirty: false });
  useEffect(() => {
    if (ctx.loading) return;
    const a = ctx.dossier?.adjustments;
    setAdj({
      empId: ctx.empId, rows: toDraftRows(a?.items),
      saved: { earn: Number(a?.earn_total || 0), deduct: Number(a?.deduct_total || 0) },
      dirty: false,
    });
  }, [ctx.dossier, ctx.empId, ctx.loading]);
  const setAdjRows = (rows) => setAdj((a) => ({ ...a, rows, dirty: true }));
  // نرخ‌نامه برای انتخابِ عملیات در ردیفِ کارمزدی — یک‌بار، وقتی ویرایشگر باز شد
  const [catalog, setCatalog] = useState([]);
  const [records, setRecords] = useState([]);
  const [negativeRecords, setNegativeRecords] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [groupFilter, setGroupFilter] = useState("all"); // all | piece | attend
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(null); // wizard-state دوره
  const [closingBusy, setClosingBusy] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [dossier, setDossier] = useState(null);       // پرونده ماهانه
  const [pipeline, setPipeline] = useState(null);
  const [newPeriodModal, setNewPeriodModal] = useState(false);
  const [newYear, setNewYear] = useState(currentJalali().year);
  const [newMonth, setNewMonth] = useState(currentJalali().month);
  const [editRec, setEditRec] = useState({ open: false, index: 0 });
  const [recForm] = Form.useForm();
  // بخش‌ها برای فیلترِ «بخش» (نامِ خوانا به‌جای id)
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    getDepartments().then((r) => setDepartments(r.data || [])).catch(() => {});
  }, []);
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  const selectedPeriodObj = periods.find(p => p.id === selectedPeriod) || null;

  // اجرای یک عملیات ویرایش/حذف با مدیریت ۴۰۳:
  // - دورهٔ تاییدشده (بدون بستن) → فقط کد مدیریتی لازم است
  // - دورهٔ در مرحلهٔ بستن (حسابداری/مدیریت/قفل) → پیامِ «ابتدا یک مرحله برگشت بزنید» نشان داده می‌شود
  const runGuardedEdit = async (attempt, okMsg) => {
    const done = async (res) => {
      message.success(typeof okMsg === "function" ? okMsg(res) : okMsg);
      // ویرایشگرِ نفر‌به‌نفر بعد از ذخیره از همین لیست دوباره می‌خواند — باید تمام شده باشد
      await handleViewRecords(selectedPeriod);
      return true;
    };
    try {
      return await done(await attempt());
    } catch (e) {
      if (e?.response?.status === 422) {
        const d = e?.response?.data?.detail;
        message.error(typeof d === "string" ? d : "ورودی نامعتبر است");
        return false;
      }
      if (e?.response?.status !== 403) { message.error(e?.response?.data?.detail || "خطا"); return false; }
      const detail = e?.response?.data?.detail || "";
      // اگر backend درخواست کد مدیریتی کرده باشد، کد بگیر و دوباره تلاش کن
      if (detail.includes("کد مدیریتی")) {
        const pin = window.prompt(detail || "کد مدیریتی:");
        if (!pin) return false;
        try {
          return await done(await attempt(pin));
        } catch (e2) { message.error(e2?.response?.data?.detail || "کد مدیریتی نادرست است"); return false; }
      }
      // قفل‌شده یا در مرحلهٔ بستن → باید مرحله را برگرداند
      message.warning(detail || "این دوره در مرحلهٔ بستن است؛ ابتدا یک مرحله برگشت بزنید.");
      return false;
    }
  };

  // باز کردن ویرایشگرِ نفر-به-نفر روی رکوردِ کلیک‌شده (ایندکس در لیستِ مرتب/فیلترشده)
  const openEditRecord = (rec) => {
    const index = filteredRecords.findIndex(r => r.id === rec.id);
    if (index < 0) return;
    if (!catalog.length) {
      getPieceRates({ active_only: true }).then((r) => setCatalog(r.data || [])).catch(() => {});
    }
    setEditRec({ open: true, index });
  };

  // پیش‌نمایشِ فیش با ناوبری بینِ نفراتِ همین لیست (به همان ترتیبِ روی صفحه)
  const payslipLabel = (r) => `${r.employee_name || r.employee_id}${r.employee_code ? ` (${r.employee_code})` : ""}`;
  const payslipResolve = (r) => ({
    url: `/api/v1/payroll/periods/${selectedPeriod}/payslip/${r.employee_id}`,
    title: `فیش حقوقی — ${r.employee_name || r.employee_id}`,
    fallbackName: `payslip_${r.employee_id}_${selectedPeriod}.pdf`,
    // «ویرایش» → همان ویرایشگرِ نفر‌به‌نفرِ رکورد (درورِ پرونده اگر باز بود بسته می‌شود)
    onEdit: () => { setDossier(null); openEditRecord(r); },
    editLabel: "ویرایش رکورد حقوق",
  });
  const previewPayslip = (employeeId) => {
    const i = filteredRecords.findIndex((r) => r.employee_id === employeeId);
    if (i >= 0) {
      pdf.open({ items: filteredRecords, index: i, resolve: payslipResolve, labelOf: payslipLabel,
        storageKey: "payslip", defaultPage: "A4" });
    } else {
      pdf.open({ url: `/api/v1/payroll/periods/${selectedPeriod}/payslip/${employeeId}`,
        title: "فیش حقوقی", fallbackName: `payslip_${employeeId}_${selectedPeriod}.pdf`,
        storageKey: "payslip", defaultPage: "A4" });
    }
  };

  /** ذخیرهٔ ردیف‌های اصلاحیِ نفرِ جاری (قبل از فیلدهای فرم؛ سرور رکورد را هم‌تراز می‌کند). */
  const saveAdjustments = async (item) => {
    if (adj.empId !== item.employee_id) return true;
    const err = validateRows(adj.rows);
    if (err) { message.error(err); return false; }
    return runGuardedEdit(
      (pin) => putPayrollAdjustments(selectedPeriod, item.employee_id, toApiItems(adj.rows), pin),
      (res) => res?.data?.message || "ردیف‌های اصلاحی ذخیره شد");
  };

  /* اصلاحِ حضور از داخلِ ویرایشِ رکورد (v2.112.0) ⇒ حقوقِ همین نفر بلافاصله دوباره حساب شود.
     رکوردِ «دستی» بدونِ تأیید بازنویسی نمی‌شود. */
  const recalcAfterAttendance = async (r) => {
    const run = (overwrite) => runGuardedEdit(
      (pin) => recalcEmployeePayroll(selectedPeriod, r.employee_id, { overwrite, admin_pin: pin })
        .then((res) => {
          if (res?.data?.manual) {
            Modal.confirm({
              title: "این رکورد دستی ویرایش شده",
              content: "حضور ذخیره شد. رکوردِ حقوق دستی ویرایش شده؛ با محاسبهٔ تازه جایگزین شود؟ (اصلاحاتِ دستیِ مبالغ پاک می‌شود؛ ردیف‌های اصلاحی و انتقالِ بدهی می‌مانند)",
              okText: "بله، دوباره حساب کن", cancelText: "نه، رکورد بماند",
              onOk: () => run(true),
            });
          }
          return res;
        }),
      (res) => (res?.data?.manual ? "حضور ذخیره شد" : res?.data?.message || "دوباره حساب شد"));
    await run(false);
    loadCtx(r.employee_id);
  };

  const handleDeleteRecord = async (rec) => {
    await runGuardedEdit(
      (pin) => deletePayrollRecord(selectedPeriod, rec.id, pin),
      "رکورد حذف شد");
  };

  const loadPeriods = async () => {
    const res = await getPeriods();
    setPeriods(res.data);
  };

  useEffect(() => { loadPeriods(); }, []);

  // اگر از پنلِ «نیازمند بررسی» با ?period_id= آمده‌ایم، همان دوره را باز کن
  useEffect(() => {
    const pid = Number(searchParams.get("period_id"));
    if (pid && periods.some((p) => p.id === pid) && selectedPeriod !== pid) {
      handleViewRecords(pid);
    } else if (!pid && !selectedPeriod && periods.length) {
      // ماهِ فعالِ مشترک (v2.132.0): همان ماهی که در صفحه‌های دیگر انتخاب شده باز شود
      const a = getActiveYm();
      const p = a && periods.find((x) => Number(x.year) === a.year && Number(x.month) === a.month);
      if (p) handleViewRecords(p.id);
    }
    // eslint-disable-next-line
  }, [periods]);

  const handleCreatePeriod = async () => {
    try {
      await createPeriod({ year: newYear, month: newMonth });
      message.success("دوره ایجاد شد");
      setNewPeriodModal(false);
      loadPeriods();
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا");
    }
  };

  const handleCalculate = async (period_id, { force = false } = {}) => {
    setLoading(true);
    try {
      const res = await calculatePayroll(period_id, { force });
      setCalcResult(res.data);
      message.success(`${res.data.calculated} پرسنل محاسبه شدند`);
      if (res.data.attendance_gate_bypassed) {
        message.warning("محاسبه بدون قفل نهایی حضور انجام شد");
      }
      loadPeriods();
      handleViewRecords(period_id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "attendance_not_finalized") {
        /* پیش‌فرض باید «برو درستش کن» باشد نه «به‌زور حساب کن» — محاسبه روی
           حضورِ تأییدنشده یعنی غیبت و کسرکارِ ناتمام مستقیم در فیش می‌نشیند. */
        const p = periods.find((x) => x.id === period_id);
        Modal.confirm({
          title: "اول حضور و غیابِ این ماه را تأیید کنید",
          content: d.message
            || "تا حضورِ ماه نهایی نشود، غیبت و کسرکارِ بررسی‌نشده وارد حقوق می‌شود.",
          okText: "رفتن به حضور و غیاب",
          cancelText: "انصراف",
          onOk: () => navigate(p
            ? `/attendance?year=${p.year}&month=${p.month}&tab=finalize`
            : "/attendance"),
          footer: (_, { OkBtn, CancelBtn }) => (
            <Space style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <CancelBtn />
              <Button danger onClick={() => {
                Modal.destroyAll();
                handleCalculate(period_id, { force: true });
              }}>محاسبهٔ اجباری (بدونِ تأییدِ حضور)</Button>
              <OkBtn />
            </Space>
          ),
        });
      } else {
        message.error(typeof d === "string" ? d : d?.message || "خطا در محاسبه");
      }
    }
    setLoading(false);
  };

  /** پرشِ سریع به صفحه‌ای که آن عدد آنجا ساخته/اصلاح می‌شود */
  const goEdit = (where) => {
    // ماهِ دورهٔ جاری همراهِ لینک می‌رود؛ وگرنه صفحهٔ مقصد ماهِ پیش‌فرضِ خودش را
    // باز می‌کند و کاربر فکر می‌کند «ماهِ اشتباه آورد».
    const p = periods.find((x) => x.id === selectedPeriod);
    const ym = p ? `year=${p.year}&month=${p.month}` : "";
    const routes = {
      attendance: `/attendance${ym ? `?${ym}` : ""}`,
      production: "/production",
      finance: "/finance?tab=ledger",
      insurance: `/insurance${ym ? `?${ym}` : ""}`,
    };
    navigate(routes[where] || "/");
  };

  const openDossier = async (rec) => {
    if (!selectedPeriod) return;
    try {
      const res = await getEmployeeDossier(selectedPeriod, rec.employee_id);
      setDossier(res.data);
      // «چرا این عدد؟» — حضور و غیاب و زنجیرهٔ محاسبه، در همین پرونده
      explainPayroll(selectedPeriod, rec.employee_id)
        .then((r) => setExplainData(r.data))
        .catch(() => setExplainData(null));
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در بارگذاری پرونده");
    }
  };

  const loadPipeline = async (period_id) => {
    try {
      const res = await getPayrollPipeline(period_id);
      setPipeline(res.data);
    } catch { setPipeline(null); }
  };

  const handleReset = (period_id) => {
    Modal.confirm({
      title: "بازگشت به وضعیت باز",
      content: "رکوردهای محاسبه‌شده پاک می‌شوند و می‌توانید دوباره محاسبه کنید. آیا مطمئنید؟",
      okText: "بله، بازگشت",
      cancelText: "انصراف",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await resetPayroll(period_id);
          message.success(res.data.message);
          setRecords([]);
          setNegativeRecords([]);
          setCalcResult(null);
          loadPeriods();
        } catch (e) {
          message.error(e?.response?.data?.detail || "خطا");
        }
      },
    });
  };

  const handleApprove = async (period_id) => {
    Modal.confirm({
      title: "تایید نهایی لیست حقوق",
      content: "آیا از تایید نهایی لیست حقوق این دوره مطمئن هستید؟ پس از تایید قابل ویرایش نیست.",
      okText: "بله، تایید می‌کنم",
      cancelText: "انصراف",
      onOk: async () => {
        await approvePayroll(period_id, 1);
        message.success("لیست حقوق تایید شد");
        loadPeriods();
        handleViewRecords(period_id);
      },
    });
  };

  const loadClosing = async (period_id) => {
    try {
      const res = await getWizardState(period_id);
      setClosing(res.data);
    } catch { setClosing(null); }
  };

  const handleViewRecords = async (period_id) => {
    setSelectedPeriod(period_id);
    rememberPeriod(periods, period_id);
    loadPipeline(period_id);
    const res = await getPayrollRecords(period_id);
    setRecords(res.data);
    loadClosing(period_id);
    // بدهکاران
    try {
      const neg = await api.get(`/api/v1/payroll/periods/${period_id}/negative-report`);
      setNegativeRecords(neg.data);
    } catch { setNegativeRecords([]); }
  };

  // ——— مراحل بستن حقوق ———
  const handleApproveAcct = async () => {
    setClosingBusy(true);
    try { await approveAccountant(selectedPeriod); message.success("تأیید حسابداری ثبت شد"); await loadClosing(selectedPeriod); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
    setClosingBusy(false);
  };

  const handleApproveMgmt = async () => {
    setClosingBusy(true);
    try { await approveManagement(selectedPeriod); message.success("تأیید مدیریت ثبت شد"); await loadClosing(selectedPeriod); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
    setClosingBusy(false);
  };

  const handleLock = async () => {
    const critical = closing?.critical_issues || 0;
    const doLock = async (force, pin) => {
      await lockPeriod(selectedPeriod, force, pin);
      message.success("دوره قفل شد");
      await loadClosing(selectedPeriod);
    };
    setClosingBusy(true);
    try {
      if (critical > 0) {
        Modal.confirm({
          title: "قفل با وجود مشکلات بحرانی",
          content: `${critical} مشکل بحرانی (حقوق منفی/بیمهٔ نامطابق) وجود دارد. قفلِ اجباری نیاز به کد مدیریتی دارد.`,
          okText: "قفل اجباری", cancelText: "انصراف", okButtonProps: { danger: true },
          onOk: async () => {
            const pin = window.prompt("کد مدیریتی برای قفل اجباری:");
            if (!pin) return;
            try { await doLock(true, pin); } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
          },
        });
      } else {
        await doLock(false);
      }
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
    setClosingBusy(false);
  };

  const handleClosingRevert = async () => {
    const cs = closing?.closing_status;
    const isUnlock = cs === "locked";
    Modal.confirm({
      title: "برگشت یک مرحله",
      content: isUnlock
        ? "بازکردن قفلِ دوره نیاز به کد مدیریتی دارد. ادامه می‌دهید؟"
        : `مرحلهٔ «${CLOSING_LABEL[cs] || cs}» لغو و یک مرحله به عقب بازمی‌گردد. پس از برگشت، رکوردها دوباره قابل ویرایش‌اند.`,
      okText: "برگشت مرحله", cancelText: "انصراف",
      onOk: async () => {
        let pin;
        if (isUnlock) { pin = window.prompt("کد مدیریتی برای بازکردن قفل:"); if (!pin) return; }
        try { await closingRevert(selectedPeriod, pin); message.success("یک مرحله به عقب بازگشت"); await loadClosing(selectedPeriod); }
        catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
      },
    });
  };

  /** v2.167.0 — کاربر: «غیرکارمزدی‌ها اولِ پارت بسته و پرداخت می‌شوند، کارمزدی‌ها بعد؛ هر کدام جدا قفل شود». */
  const handleCloseGroup = (g) => {
    Modal.confirm({
      title: `بستنِ حقوقِ ${g.label}‌ها؟`,
      icon: <LockOutlined />,
      content: (
        <div style={{ lineHeight: 2 }}>
          <div>• {fmtNum(g.count)} نفر — جمعِ خالص {fmtNum(Math.round(g.net))} ریال{g.negative ? ` (${fmtNum(g.negative)} نفر منفی)` : ""}.</div>
          <div>• حقوقشان نهایی و در حسابشان ثبت می‌شود؛ «محاسبهٔ مجدد» و ویرایش دیگر رویشان اثر ندارد.</div>
          {g.key === "piece" && <div>• کارکردِ تولیدِ این ماه قفل می‌شود (کنترلِ نهایی آزاد است).</div>}
          <div>• سندی که از این به بعد برایشان زده شود به ماهِ بعد می‌رود.</div>
          <div>• بازگشایی فقط با <b>کدِ مدیر</b>. وقتی هر دو لیست بسته شد، ماه تأییدِ نهایی می‌خورد.</div>
        </div>),
      okText: `ببند`, cancelText: "انصراف",
      onOk: async () => {
        try {
          const r = await closePayGroup(selectedPeriod, g.key);
          message.success(r.data?.message || "بسته شد");
          await loadPeriods(); await handleViewRecords(selectedPeriod);
        } catch (e) { message.error(e?.response?.data?.detail || "بستن ناموفق بود"); }
      },
    });
  };
  const handleReopenGroup = (g) => {
    Modal.confirm({
      title: `بازگشاییِ حقوقِ ${g.label}‌ها`,
      content: "حقوقشان از حساب‌ها برداشته می‌شود و دوباره قابلِ اصلاح و محاسبهٔ مجدد است. کد مدیریتی لازم است.",
      okText: "بازگشایی", cancelText: "انصراف",
      onOk: async () => {
        const pin = window.prompt("کد مدیریتی:");
        if (!pin) return;
        try {
          const r = await reopenPayGroup(selectedPeriod, g.key, pin);
          message.success(r.data?.message || "بازگشایی شد");
          await loadPeriods(); await handleViewRecords(selectedPeriod);
        } catch (e) { message.error(e?.response?.data?.detail || "بازگشایی ناموفق بود"); }
      },
    });
  };

  /** بازگشاییِ کاملِ ماه (v2.122.0): تأییدِ نهایی + مراحلِ بستن یک‌جا برمی‌گردد — با کد مدیریتی */
  const handleReopenPeriod = () => {
    const per = periods.find((p) => p.id === selectedPeriod);
    Modal.confirm({
      title: `بازگشاییِ حقوقِ ${per ? `${per.year}/${per.month}` : ""}`,
      content: "تأییدها برداشته می‌شود و ماه دوباره قابلِ اصلاح و محاسبهٔ مجدد است. بعد از اصلاح دوباره تأیید کنید. کد مدیریتی لازم است.",
      okText: "بازگشایی", cancelText: "انصراف",
      onOk: async () => {
        const pin = window.prompt("کد مدیریتی:");
        if (!pin) return;
        try {
          const r = await reopenPeriod(selectedPeriod, pin);
          message.success(r.data?.message || "ماه بازگشایی شد");
          await loadPeriods(); await loadClosing(selectedPeriod);
        } catch (e) { message.error(e?.response?.data?.detail || "بازگشایی ناموفق بود"); }
      },
    });
  };

  const handleDeletePeriod = (rec) => {
    Modal.confirm({
      title: "حذف دوره حقوقی",
      content: rec.status === "approved"
        ? "این دوره تایید شده است. حذف آن نیاز به کد مدیریتی دارد."
        : "این دوره و همه رکوردهای آن حذف می‌شوند. مطمئنید؟",
      okText: "حذف", cancelText: "انصراف", okButtonProps: { danger: true },
      onOk: async () => {
        let pin;
        if (rec.status === "approved") {
          pin = window.prompt("کد مدیریتی را وارد کنید:");
          if (!pin) return;
        }
        try {
          await deletePeriod(rec.id, pin);
          message.success("دوره حذف شد");
          if (selectedPeriod === rec.id) { setRecords([]); setNegativeRecords([]); }
          loadPeriods();
        } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
      },
    });
  };

  const periodColumns = [
    { title: "سال", dataIndex: "year", width: 70, sorter: byNum("year"),
      render: (v) => faNum(Number(v), { useGrouping: false }) },
    { title: "ماه", dataIndex: "month", render: (v) => MONTHS[v - 1], width: 100, sorter: byNum("month") },
    { title: "وضعیت", dataIndex: "status", width: 110, sorter: byStr("status"),
      render: (v) => <Tag color={statusColors[v]}>{statusLabels[v]}</Tag> },
    {
      title: "عملیات",
      render: (_, rec) => (
        <Space size={4} wrap>
          <Button size="small" onClick={() => handleViewRecords(rec.id)}>مشاهده</Button>
          {rec.status === "open" && (
            <Button size="small" type="primary" icon={<CalculatorOutlined />} loading={loading}
              onClick={() => handleCalculate(rec.id)}>محاسبه</Button>
          )}
          {rec.status === "calculated" && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />}
                style={{ background: "#107C41" }} onClick={() => handleApprove(rec.id)}>تایید</Button>
              <Button size="small" icon={<ReloadOutlined />}
                onClick={() => handleReset(rec.id)}>محاسبه مجدد</Button>
            </>
          )}
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeletePeriod(rec)} />
        </Space>
      ),
    },
  ];

  // کارمزدی = piece ؛ حضوری = fixed/daily
  /** گروه‌های دریافتیِ یک نفر — انحصاری نیست: کارمزدی که پایهٔ تکمیلی هم دارد
   *  در هر دو گروه پیدا می‌شود، و رانندهٔ کارمزدی هم در «راننده». */
  const groupsOf = (r) => {
    const g = [];
    if (r.pay_model === "piece") g.push("piece");
    if (r.pay_model === "fixed") g.push("fixed");
    if (r.pay_model === "daily") g.push("daily");
    if (r.pay_model === "none") g.push("none");
    if (Number(r.base_supplement) > 0 && r.pay_model === "piece") g.push("fixed");
    if (Number(r.driver_earnings) > 0 || r.is_driver) g.push("driver");
    if (r.attendance_required) g.push("attend");
    return g.length ? [...new Set(g)] : ["attend"];
  };
  const groupOf = (r) => (r.pay_model === "piece" ? "piece" : "attend");
  const closedGroupKeys = new Set((closing?.groups || []).filter((g) => g.closed).map((g) => g.key));
  /* دستهٔ حقوقی (خواستهٔ کاربر ۱۴۰۵/۰۶/۲۸): ثابت › روزمزد › کارمزدی › راننده › مدیر کارگاه › بدون پایه */
  const roleOf = (r) => {
    if (r.is_workshop_manager) return "manager";
    if (r.is_driver && r.pay_model !== "fixed") return "driver";
    if (r.pay_model === "fixed") return r.is_driver ? "driver" : "fixed";
    if (r.pay_model === "daily") return "daily";
    if (r.pay_model === "piece") return "piece";
    return "none";
  };
  const ROLE_LABEL = { fixed: "حقوق ثابت", daily: "روزمزد", piece: "کارمزدی", driver: "راننده", manager: "مدیر کارگاه", none: "بدون پایه" };
  const ROLE_COLOR = { fixed: "green", daily: "cyan", piece: "geekblue", driver: "purple", manager: "volcano", none: "default" };
  const ROLE_RANK = { fixed: 0, daily: 1, piece: 2, driver: 3, manager: 4, none: 5 };
  /* «حقوق پایه» = فقط سهمِ پایه: ناخالص منهای کارمزد، راننده و ردیف‌های اصلاحی — کارمزدی‌ها
     دیگر عددِ کارمزدشان را در ستونِ پایه تکرار نمی‌بینند. */
  const basePayOf = (r) => Math.max(0, Number(r.gross_pay || 0) - Number(r.piece_earnings || 0)
    - Number(r.driver_earnings || 0) - Number(r.adjustment_earnings || 0) - Number(r.secondary_pay_amount || 0));
  const groupRank = (r) => ROLE_RANK[roleOf(r)] ?? 9;
  const codeKey = (r) => {
    const c = (r.employee_code || "").toString().trim();
    const n = parseInt(c, 10);
    return Number.isNaN(n) ? [1, 0, c] : [0, n, ""];
  };

  const filteredRecords = records
    .filter(r => !searchText
      || (r.employee_name || "").includes(searchText)
      || (r.employee_code || "").toString().includes(searchText))
    .filter(r => groupFilter === "all" || groupOf(r) === groupFilter)
    .filter(r => !onlyNegative || r.is_negative_warning)
    .slice()
    .sort((a, b) => {
      if (groupRank(a) !== groupRank(b)) return groupRank(a) - groupRank(b);
      // داخلِ هر دسته: خالص‌های صفر اول (باید بررسی شوند)، بعد کد
      const az = Number(a.net_pay || 0) === 0 ? 0 : 1, bz = Number(b.net_pay || 0) === 0 ? 0 : 1;
      if (az !== bz) return az - bz;
      const [ax, an, as] = codeKey(a), [bx, bn, bs] = codeKey(b);
      if (ax !== bx) return ax - bx;
      if (an !== bn) return an - bn;
      if (as !== bs) return as < bs ? -1 : 1;
      return (a.employee_name || "").localeCompare(b.employee_name || "", "fa");
    });
  // (باید بعد از filteredRecords بیاید — v2.144.1: بالاتر بودنش صفحه را می‌شکست)
  /* v2.144.0 — از «اسناد مالی» روی قلمِ حقوقیِ یک نفر ⇒ همین‌جا ویرایشگرِ همان نفر باز شود (?employee_id=) */
  const [empOpened, setEmpOpened] = useState(null);
  useEffect(() => {
    const eid = Number(searchParams.get("employee_id"));
    if (!eid || empOpened === eid || !filteredRecords.length) return;
    const rec = filteredRecords.find((r) => r.employee_id === eid);
    if (rec) { setEmpOpened(eid); openEditRecord(rec); }
    // eslint-disable-next-line
  }, [filteredRecords, searchParams]);


  const pieceCount = records.filter(r => groupOf(r) === "piece").length;
  const attendCount = records.length - pieceCount;

  /* ترتیبِ ستون‌ها = ترتیبی که حسابدار می‌خواند (خواستهٔ کاربر):
     نام ← کد ← نوع حقوق ← بیمه ← پایه ← کارمزد ← کسرکار ← غیبت ← حق بیمه ←
     پرداختی بیمه ← بقیه. ستون‌های کم‌کاربرد ته می‌روند و پیش‌فرض خاموش‌اند. */
  const recordColumns = [
    { title: "پرسنل", dataIndex: "employee_name", width: 150, fixed: "right",
      sorter: byStr("employee_name"),
      // کلیک روی نام ⇒ پروندهٔ حقوقی، که خودش «چرا این عدد؟» را هم دارد
      render: (v, r) => <span><a onClick={() => openDossier(r)}>{v || `ID:${r.employee_id}`}</a><NewStaffTag rec={r} />
        {/* v2.167.0: لیستِ گروهِ این نفر بسته شده */}
        {closedGroupKeys.has(groupOf(r) === "piece" ? "piece" : "fixed") && (
          <LockOutlined title="حقوقِ این گروه بسته شده" style={{ color: "#107C41", marginInlineStart: 4 }} />)}</span> },
    { title: "کد", dataIndex: "employee_code", width: 64, sorter: byStr("employee_code"),
      render: v => v || "—" },
    // v2.132.0: بیمه‌ای/غیربیمه‌ای و سمت
    { title: "بیمه", dataIndex: "insured_label", width: 100,
      filters: [{ value: "بیمه‌ای", text: "بیمه‌ای" }, { value: "غیربیمه‌ای", text: "غیربیمه‌ای" }],
      onFilter: (v, r) => r.insured_label === v,
      render: (v) => (v ? <Tag color={v === "بیمه‌ای" ? "blue" : "default"} style={{ margin: 0 }}>{v}</Tag> : "—") },
    { title: "سمت", dataIndex: "job_title", width: 120, render: (v) => v || "—" },
    { title: "دسته", dataIndex: "role_group", width: 105, gxFilter: "enum",
      gxFilterGet: (r) => ROLE_LABEL[roleOf(r)],
      render: (_, r) => <Tag color={ROLE_COLOR[roleOf(r)]} style={{ margin: 0 }}>{ROLE_LABEL[roleOf(r)]}</Tag> },
    { title: "نوع حقوق", dataIndex: "pay_model", width: 110,
      // چندانتخابی و غیرانحصاری — کسی که هم کارمزد دارد هم پایهٔ ثابت، در هر دو می‌آید
      filters: [
        { text: "کارمزدی", value: "piece" },
        { text: "حقوق ثابت", value: "fixed" },
        { text: "راننده", value: "driver" },
        { text: "مشمولِ حضور", value: "attend" },
      ],
      onFilter: (val, r) => groupsOf(r).includes(val),
      render: v => <Tag color={v === "piece" ? "geekblue" : v === "none" ? "default" : "green"}>{PAY_MODEL_LABEL[v] || v || "—"}</Tag> },
    { title: "بیمه", dataIndex: "has_insurance", width: 90,
      gxFilter: "bool", gxFilterLabels: { yes: "بیمه دارد", no: "بیمه ندارد" },
      render: (v) => (v ? <Tag color="blue">دارد</Tag> : <Tag>ندارد</Tag>) },
    { title: "بخش", dataIndex: "department_id", width: 130,
      gxFilter: "enum",
      gxFilterGet: (r) => deptName(r.department_id),
      render: (v) => deptName(v) },
    /* v2.152.4 (کاربر): «حقوق پایه همان است که در بخشِ پرسنل برایش نوشته شده» ⇒ ستونِ «حقوق پایه» از پرونده (ماهانه؛
       روزمزد: نرخِ روزانه). سهمِ کارکردِ همین ماه (پس از غیبت/کسرکار) جدا در «پایهٔ کارکردِ ماه». */
    { title: "حقوق پایه", dataIndex: "profile_monthly_salary", width: 120,
      sorter: (a, b) => (Number(a.profile_monthly_salary) || 0) - (Number(b.profile_monthly_salary) || 0),
      render: (v, r) => (Number(v) > 0 ? <b>{fmt(v)}</b>
        : Number(r.profile_daily_rate) > 0
          ? <span title="روزمزد — نرخِ هر روز">{fmt(r.profile_daily_rate)} <Text type="secondary" style={{ fontSize: 11 }}>/ روز</Text></span>
          : <Text type="secondary">—</Text>) },
    { title: "پایهٔ کارکردِ ماه", dataIndex: "gross_pay", width: 120,
      sorter: (a, b) => basePayOf(a) - basePayOf(b),
      render: (_, r) => (basePayOf(r) > 0
        ? <span title="سهمِ پایه در همین ماه — پس از کسرِ غیبت/کسرکار">{fmt(basePayOf(r))}</span>
        : <Text type="secondary">—</Text>) },
    { title: "درآمد کارمزد", dataIndex: "piece_earnings", width: 120,
      sorter: byNum("piece_earnings"),
      // کارمزد از ثبت تولید می‌آید — مستقیم برو همان‌جا
      render: (v) => (Number(v) > 0
        ? <a onClick={() => goEdit("production")} title="اصلاح در ثبت و مدیریت تولید">{fmt(v)}</a>
        : fmt(v)) },
    { title: "روزکارکرد", dataIndex: "worked_days", width: 90, sorter: byNum("worked_days"),
      /* کارمزدیِ غیرمشمولِ حضور اصلاً روزِ کارکرد ندارد — عددِ صفر گمراه‌کننده
         است (خواستهٔ صریحِ کاربر: «یه خط تیره باشه که مشخص باشه توی حضور نیست»). */
      render: (v, r) => (!r.attendance_required
        ? <Tooltip title="این پرسنل مشمولِ حضور و غیاب نیست؛ حقوقش از کارکردِ تولید می‌آید">
          <Text type="secondary">—</Text></Tooltip>
        : <a onClick={() => goEdit("attendance")} title="اصلاح در حضور و غیاب">{fmt(v)}</a>) },
    { title: "کسر کسری", dataIndex: "shortage_deduction", width: 100,
      render: (v, r) => (!r.attendance_required ? <Text type="secondary">—</Text>
        : (Number(v) > 0
          ? <a onClick={() => goEdit("attendance")} title="بررسی/بخشش در حضور و غیاب">{fmt(v)}</a>
          : fmt(v))) },
    { title: "کسر غیبت", dataIndex: "absence_deduction", width: 100,
      render: (v, r) => (!r.attendance_required ? <Text type="secondary">—</Text>
        : (Number(v) > 0
          ? <a onClick={() => goEdit("attendance")} title="بررسی/بخشش در حضور و غیاب">{fmt(v)}</a>
          : fmt(v))) },
    { title: "اضافه‌کاری", dataIndex: "overtime_days", render: fmt, width: 90, sorter: byNum("overtime_days") },
    { title: "روز اضافه (۳۱)", dataIndex: "extra_day_amount", render: fmt, width: 100 },
    { title: "درآمد راننده", dataIndex: "driver_earnings", render: fmt, width: 100 },
    { title: "بدهی قبلی", dataIndex: "previous_debt_applied", render: fmt, width: 100 },
    { title: "حق بیمه", dataIndex: "insurance_premium", width: 90,
      render: (v, r) => (Number(v || r.insurance_deduction) > 0
        ? <a onClick={() => goEdit("insurance")} title="لیست بیمه">{fmt(v || r.insurance_deduction)}</a>
        : fmt(v || r.insurance_deduction)) },
    { title: "پرداختی بیمه", dataIndex: "insurance_registered_payment", render: fmt, width: 100 },
    /* ماندهٔ حسابِ پیش از ماه (v2.113.0): مثبت = بدهیِ قبلی که کسر شد، منفی = بستانکاریِ قبلی */
    { title: "مانده از قبل", dataIndex: "opening_balance", sorter: byNum("opening_balance"), align: "left",
      render: (v) => (!Number(v) ? <Text type="secondary">—</Text>
        : <Tooltip title={Number(v) > 0 ? "بدهیِ پیش از این ماه — کسر شد (ریز در ویرایش/فیش)" : "بستانکاریِ پیش از این ماه — اضافه شد"}>
            <b style={{ color: Number(v) > 0 ? "#C0392B" : "#107C41" }}>{formatMoney(Math.abs(Number(v)))}{Number(v) < 0 ? " +" : ""}</b>
          </Tooltip>) },
    { title: "مساعده", dataIndex: "advance_deduction", sorter: byNum("advance_deduction"),
      render: (v) => (Number(v) > 0
        ? <a onClick={() => goEdit("finance")} title="اصلاح در دفتر مالی">{fmt(v)}</a> : fmt(v)) },
    { title: "خرید محصول", dataIndex: "product_purchase_deduction", width: 100,
      render: (v) => (Number(v) > 0
        ? <a onClick={() => goEdit("finance")} title="اصلاح در دفتر مالی">{fmt(v)}</a> : fmt(v)) },
    { title: "بوفه", dataIndex: "buffet_deduction", sorter: byNum("buffet_deduction"),
      render: (v) => (Number(v) > 0
        ? <a onClick={() => goEdit("finance")} title="اصلاح در دفتر مالی">{fmt(v)}</a> : fmt(v)) },
    { title: "جریمه", dataIndex: "penalty_deduction", sorter: byNum("penalty_deduction"),
      render: (v, r) => (Number(v || r.manual_deduction) > 0
        ? <a onClick={() => goEdit("finance")} title="اصلاح در دفتر مالی">{fmt(v || r.manual_deduction)}</a>
        : fmt(v || r.manual_deduction)) },
    { title: "انتقالی ماه بعد", dataIndex: "debt_carried_forward", render: fmt, width: 110 },
    {
      title: "حقوق خالص",
      dataIndex: "net_pay",
      sorter: byNum("net_pay"),
      render: (v, rec) => (
        <span style={{ color: rec.is_negative_warning ? "red" : "#107C41", fontWeight: "bold" }}>
          {rec.is_negative_warning && <WarningOutlined style={{ marginLeft: 4 }} />}
          {fmt(v)}
        </span>
      ),
    },
    {
      title: "وضعیت",
      dataIndex: "status",
      width: 90,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={v === "final" ? "green" : "default"}>{v === "final" ? "نهایی" : "پیش‌نویس"}</Tag>
          {r.calculation_mode === "accounting_v1" && <Tag color="blue" style={{ fontSize: 10 }}>v1</Tag>}
        </Space>
      ),
    },
    {
      title: "فیش PDF",
      width: 80,
      render: (_, rec) => (
        <Tooltip title="پیش‌نمایش و دانلود فیش حقوقی PDF">
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            style={{ color: "#C0392B", borderColor: "#C0392B" }}
            onClick={() => previewPayslip(rec.employee_id)}
          />
        </Tooltip>
      ),
    },
    {
      title: "عملیات", width: 100,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title="پرونده ماهانه / فیش">
            <Button size="small" icon={<ProfileOutlined />} onClick={() => openDossier(rec)} />
          </Tooltip>
          <Tooltip title="ویرایش دستی مبالغ">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditRecord(rec)} />
          </Tooltip>
          <Popconfirm title="این رکورد حذف شود؟" onConfirm={() => handleDeleteRecord(rec)}
            okText="بله" cancelText="خیر">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const negCols = [
    { title: "پرسنل", dataIndex: "employee_name", sorter: byStr("employee_name") },
    { title: "حقوق ناخالص (ریال)", dataIndex: "gross_pay", render: fmt, sorter: byNum("gross_pay") },
    { title: "جمع کسورات (ریال)", dataIndex: "total_deductions", sorter: byNum("total_deductions"),
      render: v => <span style={{ color: "#C0392B" }}>{fmt(v)}</span> },
    { title: "حقوق خالص (ریال)", dataIndex: "net_pay", sorter: byNum("net_pay"),
      render: v => <b style={{ color: "red" }}>{fmt(v)}</b> },
    { title: "کسری (ریال)", dataIndex: "deficit", sorter: byNum("deficit"),
      render: v => <Tag color="red">{fmt(v)}</Tag> },
  ];

  const totalNet = records.reduce((s, r) => s + Number(r.net_pay || 0), 0);
  const warnings = records.filter((r) => r.is_negative_warning).length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Space>
          <Title level={5} style={{ margin: 0 }}>حقوق و دستمزد</Title>
          <HelpPopover title="حقوق از کجا می‌آید؟" content={
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              هر پرسنل <b>یک مدلِ حقوق</b> دارد و منبعِ عددش فرق می‌کند:
              <ul style={{ paddingInlineStart: 18, margin: "6px 0" }}>
                <li><b>ثابت</b> → حقوق ماهانه؛ غیبت/کسری از <b>دستگاه حضور</b> کم می‌شود
                  (فقط اگر «مشمولِ دستگاه حضور» تیک داشته باشد).</li>
                <li><b>کارمزد</b> → جمعِ کارکردِ ثبت‌شده در <b>ثبت و مدیریت تولید</b>.
                  حضور و غیاب اصلاً در آن اثر ندارد.</li>
              </ul>
              <b>کارمزدی صفر شده؟</b> این سه را به‌ترتیب چک کنید:
              <ol style={{ paddingInlineStart: 18, margin: "6px 0" }}>
                <li>مدلِ حقوقِ آن پرسنل واقعاً «کارمزد» باشد.</li>
                <li>کارکرد در تبِ «عملیات و کارکرد» ثبت شده باشد (نه فقط کنترل اول/نهایی —
                  کنترل هیچ حقوقی نمی‌سازد).</li>
                <li><b>ماهِ محاسبهٔ آن پارت</b> با ماهی که دارید حساب می‌کنید یکی باشد.
                  اگر خالی باشد، ملاک <b>تاریخِ کار</b> است.</li>
              </ol>
              «نقش‌ها و بازه‌ها»ی پرسنل (ویرایش ← اطلاعات حقوقی): نقشِ <b>ترکیبی</b> (سرایداری…)
              روی همین حقوق اضافه می‌شود؛ بازهٔ <b>تکی</b> (مثلاً روزمزد از یک تاریخ) برای همان روزها
              جای مدلِ اصلی را می‌گیرد و کارِ تولیدِ آن روزها شمرده نمی‌شود.
            </div>
          } />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewPeriodModal(true)}>
          دوره جدید
        </Button>
      </div>

      {pipeline && selectedPeriod && (
        <Card size="small" title="مراحل دوره (Pipeline)" style={{ marginBottom: 12 }}>
          <Steps size="small" current={Math.max(0, (pipeline.stages || []).findIndex((x) => x.key === pipeline.pipeline_stage))}
            items={(pipeline.stages || []).map((st) => ({
              title: st.label,
              status: st.done ? (st.key === pipeline.pipeline_stage ? "process" : "finish") : "wait",
            }))}
          />
          <div style={{ marginTop: 8, fontSize: 12 }}>
            حضور ماه: <Tag color={pipeline.attendance_monthly_status === "finalized" ? "green" : "orange"}>
              {pipeline.attendance_monthly_status === "finalized" ? "قفل‌شده" : (pipeline.attendance_monthly_status || "ثبت نشده")}
            </Tag>
            مرحله فعلی: <Tag color="blue">{pipeline.pipeline_label}</Tag>
          </div>
        </Card>
      )}

      {/* دوره‌ها تمامِ عرض را می‌گیرند و جزئیات زیرشان می‌آید؛ قبلاً در ستونِ ۹/۲۴
          فشرده می‌شد و ستون‌ها اسکرولِ افقی می‌خوردند در حالی که نیمی از صفحه خالی بود. */}
      <Row gutter={16}>
        <Col xs={24}>
          <Card title="دوره‌های حقوقی" style={{ marginBottom: 16 }}>
            <GxTable dataSource={periods} columns={periodColumns} rowKey="id"
              size="small" pagination={false} scroll={{ x: "max-content" }}
              rowClassName={rec => rec.id === selectedPeriod ? "ant-table-row-selected" : ""}
            />
          </Card>
        </Col>

        <Col xs={24}>
          {calcResult && (
            <Alert
              type={calcResult.negative_warnings?.length > 0 ? "warning" : "success"}
              message={`محاسبه انجام شد — ${calcResult.calculated} پرسنل`}
              description={calcResult.negative_warnings?.length > 0
                ? `هشدار حقوق منفی: ${calcResult.negative_warnings.length} نفر`
                : "همه حقوق‌ها مثبت هستند"}
              showIcon style={{ marginBottom: 12 }} closable
            />
          )}

          {records.length > 0 && (
            <>
              <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col xs={24} sm={8}>
                  <Card size="small">
                    <Statistic title="تعداد پرسنل" value={faNum(records.length)} />
                  </Card>
                </Col>
                <Col xs={12} sm={8}>
                  <Card size="small">
                    <Statistic title="جمع حقوق خالص (ریال)" value={faNum(totalNet)} />
                  </Card>
                </Col>
                <Col xs={12} sm={8}>
                  <Card size="small">
                    <Statistic title="هشدار حقوق منفی" value={faNum(Number(warnings))}
                      valueStyle={{ color: warnings > 0 ? "red" : "#107C41" }} />
                  </Card>
                </Col>
              </Row>

              {/* ——— مراحل بستن حقوق ——— */}
              {(() => {
                const cs = closing?.closing_status ?? null;
                const calcDone = (closing?.records_count || records.length) > 0;
                const csIdx = CLOSING_STEPS.indexOf(cs); // 0..3
                const current = !calcDone ? 0 : csIdx + 1; // 0محاسبه 1حسابداری 2مدیریت 3قفل (۴=قفل‌شده)
                const locked = cs === "locked";
                const crit = closing?.critical_issues || 0;
                const totalIssues = closing?.total_issues || 0;
                return (
                  <Card
                    size="small"
                    style={{ marginBottom: 12 }}
                    title={<Space><ClockCircleOutlined /> مراحل بستن حقوق ماه</Space>}
                    extra={
                      <Button size="small" icon={<EditOutlined />} onClick={() => navigate("/attendance")}>
                        بررسی/اصلاحِ حضورِ این ماه
                      </Button>
                    }
                  >
                    <Steps
                      size="small"
                      current={current}
                      style={{ marginBottom: 12 }}
                      items={[
                        { title: "محاسبه", description: calcDone ? "انجام شد" : "در انتظار" },
                        { title: "تأیید حسابداری" },
                        { title: "تأیید مدیریت" },
                        { title: "قفل نهایی" },
                      ]}
                    />

                    {/* v2.167.0 — دو لیستِ ماه، هر کدام جدا بسته می‌شود */}
                    {calcDone && (closing?.groups || []).length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: 8, marginBottom: 12 }}>
                        {closing.groups.map((g) => (
                          <Card key={g.key} size="small"
                            style={g.closed ? { borderColor: "#107C41", background: "rgba(16,124,65,.06)" } : undefined}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                              gap: 8, flexWrap: "wrap" }}>
                              <div>
                                <b>لیستِ {g.label}</b>
                                <div style={{ fontSize: 12, opacity: 0.75 }}>
                                  {fmtNum(g.count)} نفر — خالص {fmtNum(Math.round(g.net))} ریال
                                  {g.negative ? ` — ${fmtNum(g.negative)} منفی` : ""}
                                </div>
                                {g.closed && g.closed_at && (
                                  <div style={{ fontSize: 12, opacity: 0.75 }}>
                                    بسته شد {toJalali(g.closed_at)}{g.closed_by ? ` — ${g.closed_by}` : ""}
                                  </div>
                                )}
                              </div>
                              {g.closed ? (
                                <Space size={4}>
                                  <Tag color="green" icon={<LockOutlined />} style={{ margin: 0 }}>بسته — آمادهٔ پرداخت</Tag>
                                  {!cs && (
                                    <Button size="small" icon={<UnlockOutlined />} onClick={() => handleReopenGroup(g)}>
                                      بازگشایی</Button>
                                  )}
                                </Space>
                              ) : (
                                <Button type="primary" size="small" icon={<LockOutlined />} disabled={!g.count}
                                  onClick={() => handleCloseGroup(g)}>بستنِ {g.label}‌ها</Button>
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}

                    {locked ? (
                      <Alert type="success" showIcon style={{ marginBottom: 12 }}
                        icon={<LockOutlined />}
                        message="این دوره قفل شده است."
                        description="برای هرگونه ویرایش رکورد، ابتدا قفل را باز کنید (کد مدیریتی لازم است)." />
                    ) : totalIssues > 0 ? (
                      <Alert type={crit > 0 ? "error" : "warning"} showIcon style={{ marginBottom: 12 }}
                        message={`${totalIssues} مورد نیازمند بررسی${crit > 0 ? ` — شامل ${crit} مشکل بحرانی` : ""}`}
                        description={
                          /* هر مورد مستقیم به صفحه‌ای می‌رود که همان‌جا حل می‌شود */
                          <Space direction="vertical" size={4} style={{ width: "100%", marginTop: 4 }}>
                            {(closing?.steps || []).filter(s => s.issue_count > 0).map(s => (
                              <div key={s.key} style={{ display: "flex", alignItems: "center",
                                gap: 8, flexWrap: "wrap" }}>
                                <Tag color={s.key === "payslips" || s.key === "insurance" ? "red" : "orange"}>
                                  {fmtNum(s.issue_count)}
                                </Tag>
                                <span>{s.title}</span>
                                {s.hint && (
                                  <Text type="secondary" style={{ fontSize: 12 }}>({s.hint})</Text>
                                )}
                                {s.route && (
                                  <Button type="link" size="small" style={{ padding: "0 4px" }}
                                    onClick={() => navigate(s.route)}>
                                    {s.action || "رفتن و اصلاح"} ←
                                  </Button>
                                )}
                              </div>
                            ))}
                          </Space>
                        } />
                    ) : calcDone ? (
                      <Alert type="success" showIcon style={{ marginBottom: 12 }}
                        message="موردِ بحرانی‌ای برای بستن یافت نشد." />
                    ) : null}

                    <Space wrap>
                      {!calcDone && <Text type="secondary">ابتدا دوره را از لیست محاسبه کنید.</Text>}

                      {calcDone && cs === null && (
                        <Button type="primary" icon={<CheckOutlined />} loading={closingBusy}
                          onClick={handleApproveAcct}>تأیید حسابداری</Button>
                      )}

                      {cs === "accountant_approved" && (
                        <>
                          <Button type="primary" icon={<CheckOutlined />} loading={closingBusy}
                            style={{ background: "#107C41" }} onClick={handleApproveMgmt}>تأیید مدیریت</Button>
                          <Button icon={<RollbackOutlined />} onClick={handleClosingRevert}>برگشت مرحله</Button>
                        </>
                      )}

                      {cs === "management_approved" && (
                        <>
                          <Button type="primary" danger icon={<LockOutlined />} loading={closingBusy}
                            onClick={handleLock}>قفل نهایی</Button>
                          <Button icon={<RollbackOutlined />} onClick={handleClosingRevert}>برگشت مرحله</Button>
                        </>
                      )}

                      {locked && (
                        <Button icon={<RollbackOutlined />} onClick={handleClosingRevert}>بازکردن قفل</Button>
                      )}
                      {(cs || ["approved", "paid"].includes(periods.find((p) => p.id === selectedPeriod)?.status)) && (
                        <Button danger icon={<UnlockOutlined />} onClick={handleReopenPeriod}>
                          بازگشاییِ ماه (کد مدیریتی)
                        </Button>
                      )}
                    </Space>
                  </Card>
                );
              })()}

              <GxTabs urlKey="tab" defaultActiveKey="list" items={[
                {
                  key: "list",
                  label: "لیست حقوق",
                  children: (
                    <Card extra={
                      <Space wrap>
                        {/* وقتی از لینکِ «فیش‌های منفی» آمده‌ایم، فیلتر باید دیده و قابلِ حذف باشد */}
                        {onlyNegative && (
                          <Tag color="red" closable onClose={() => setOnlyNegative(false)}>
                            فقط فیش‌های منفی ({fmtNum(warnings)})
                          </Tag>
                        )}
                        <Input
                          prefix={<SearchOutlined />}
                          placeholder="جستجوی نام/کد..."
                          value={searchText}
                          onChange={e => setSearchText(e.target.value)}
                          allowClear
                          style={{ width: 150 }}
                          size="small"
                        />
                        <Select
                          size="small"
                          value={groupFilter}
                          onChange={setGroupFilter}
                          style={{ width: 190 }}
                          options={[
                            { value: "all", label: `همه (${records.length})` },
                            /* v2.152.10 (کاربر): «ثابت‌ها — هر کس کارمزدی نیست — جدا بیایند تا زودتر محاسبه و پرداخت شوند» */
                            { value: "attend", label: `ثابت / غیرکارمزدی (${attendCount})` },
                            { value: "piece", label: `کارمزدی (${pieceCount})` },
                          ]}
                        />
                        {/* v2.152.11 (کاربر): «لیست فقط همان‌هایی که فیلتر کرده‌ایم؛ گزینهٔ همگانی هم باشد» — کلیکِ اصلی =
                            فیلترِ فعلیِ صفحه (جستجو/گروه/منفی‌ها)، ▾ = همهٔ نفرات. لیستِ کامل: بقیه در بخشِ کوچکِ «لیستِ ناقص» ته فایل. */}
                        {[
                          { url: "/api/v1/export/payment", label: "لیست پرداخت (حسابداری)", color: "#107C41" },
                          { url: "/api/v1/export/insurance", label: "لیست بیمه", color: "#17a2b8" },
                          { url: "/api/v1/export/payroll", label: "لیست کامل حقوق", color: "#9E6A00" },
                        ].map((x) => {
                          const filtered = filteredRecords.length !== records.length;
                          const run = async (all, accounts) => {
                            if (x.url.endsWith("/insurance") && accounts === undefined) {
                              return setInsAsk({ run: (opt) => run(all, opt) });
                            }
                            const ids = !all && filtered ? filteredRecords.map((r) => r.employee_id).join(",") : undefined;
                            if (!all && filtered && !ids) return message.warning("در فیلترِ فعلی کسی نیست");
                            /* v2.155.0 (کاربر): «بالای لیست بنویسه لیست غیرکارمزدی/کارمزدی؛ تو اسمِ فایل هم» — سرور سرتیتر و نامِ فایل را می‌سازد */
                            const group = !all && groupFilter !== "all" ? groupFilter : undefined;
                            const gLabel = group === "piece" ? " کارمزدی" : group === "attend" ? " غیرکارمزدی" : "";
                            try {
                              await downloadFile(x.url, `${x.label}${gLabel}.xlsx`,
                                { params: { period_id: selectedPeriod, ...(ids ? { employee_ids: ids } : {}),
                                  ...(group ? { group } : {}),
                                  ...(accounts ? { accounts: accounts.accounts, unpaid_only: accounts.unpaid_only } : {}) } });
                              message.success(all || !filtered ? "فایلِ همهٔ نفرات دانلود شد"
                                : `فایلِ ${filteredRecords.length} نفرِ فیلترشده دانلود شد`);
                            } catch { message.error("خطا در ساختِ خروجی"); }
                          };
                          return (
                            <Dropdown.Button key={x.url} size="small" onClick={() => run(false)}
                              buttonsRender={([left, right]) => [
                                React.cloneElement(left, { style: { background: x.color, color: "#fff", borderColor: x.color } }),
                                React.cloneElement(right, { style: { background: x.color, color: "#fff", borderColor: x.color } }),
                              ]}
                              menu={{ items: [{ key: "all", label: `همهٔ نفرات (${records.length})` }], onClick: () => run(true) }}>
                              <FileExcelOutlined /> {x.label}{filtered ? ` — ${filteredRecords.length} نفر` : ""}
                            </Dropdown.Button>
                          );
                        })}
                        <InsuranceListModal open={!!insAsk} onCancel={() => setInsAsk(null)}
                          onOk={(opt) => { const r = insAsk; setInsAsk(null); r.run(opt); }} />
                      </Space>
                    }>
                      {/* جدول عمداً خلوت باز می‌شود: ستون‌های کم‌کاربرد بسته‌اند
                          و با دکمهٔ «ستون‌ها» باز می‌شوند (انتخاب ذخیره می‌ماند). */}
                      <GxTable
                        layoutId="payroll-records"
                        dataSource={filteredRecords}
                        columns={recordColumns}
                        gxDefaultHidden={[
                          "overtime_days", "extra_day_amount", "driver_earnings",
                          "previous_debt_applied",
                          "product_purchase_deduction", "debt_carried_forward",
                        ]}
                        rowKey="id"
                        size="small"
                        scroll={{ x: "max-content" }}
                        pagination={{ pageSize: 20 }}
                        rowClassName={rec => rec.is_negative_warning ? "ant-table-row-selected" : ""}
                      />
                    </Card>
                  ),
                },
                {
                  key: "negative",
                  label: (
                    <span>
                      هشدارها
                      {negativeRecords.length > 0 && (
                        <Tag color="red" style={{ marginRight: 6 }}>{negativeRecords.length}</Tag>
                      )}
                    </span>
                  ),
                  children: (
                    <Card extra={negativeRecords.length > 0 && (
                      <ExportButton
                        url="/api/v1/export/debtors"
                        params={{ period_id: selectedPeriod }}
                        label="خروجی بدهکاران"
                        color="#C0392B"
                      />
                    )}>
                      {negativeRecords.length === 0 ? (
                        <Alert type="success" showIcon message="همه حقوق‌ها مثبت هستند ✓" />
                      ) : (
                        <>
                          <Alert
                            type="warning"
                            showIcon
                            message={`${negativeRecords.length} نفر دارای حقوق منفی هستند`}
                            description="این افراد کسوراتی بیش از حقوق ناخالص دارند. کسری باید به ماه بعد منتقل شود یا تنظیم شود."
                            style={{ marginBottom: 12 }}
                          />
                          <GxTable
                            dataSource={negativeRecords}
                            columns={negCols}
                            rowKey="employee_id"
                            size="small"
                            pagination={false}
                          />
                        </>
                      )}
                    </Card>
                  ),
                },
              ]} />
            </>
          )}

          {records.length === 0 && !calcResult && (
            <EmptyWorkArea
              minHeight={220}
              message="یک دوره حقوقی را از جدولِ بالا انتخاب کنید تا جزئیات نمایش داده شود."
            />
          )}
        </Col>
      </Row>

      {/* Modal دوره جدید */}
      <Modal
        title="ایجاد دوره حقوقی جدید"
        open={newPeriodModal}
        onOk={handleCreatePeriod}
        onCancel={() => setNewPeriodModal(false)}
        okText="ایجاد دوره"
        cancelText="انصراف"
      >
        <div style={{ direction: "rtl", paddingTop: 12 }}>
          <Row gutter={12}>
            <Col span={12}>
              <div style={{ marginBottom: 8 }}><Text strong>سال شمسی:</Text></div>
              <Select value={newYear} onChange={setNewYear} style={{ width: "100%" }}>
                {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
              </Select>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 8 }}><Text strong>ماه:</Text></div>
              <Select value={newMonth} onChange={setNewMonth} style={{ width: "100%" }}>
                {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
              </Select>
            </Col>
          </Row>
        </div>
      </Modal>

      {/* ویرایشگرِ نفر-به-نفرِ رکوردهای حقوق */}

      <Drawer
        title={dossier ? `پرونده ماهانه — ${dossier.employee?.full_name}` : "پرونده"}
        open={!!dossier}
        onClose={() => setDossier(null)}
        width={520}
        extra={dossier && (
          <Space>
            <Button size="small" icon={<FilePdfOutlined />}
              onClick={() => previewPayslip(dossier.employee.id)}>فیش PDF</Button>
          </Space>
        )}
      >
        {dossier && (
          <>
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="کد">{dossier.employee.employee_code}</Descriptions.Item>
              <Descriptions.Item label="مدل حقوق">{PAY_MODEL_LABEL[dossier.employee.pay_model] || dossier.employee.pay_model}</Descriptions.Item>
              <Descriptions.Item label="سمت">{dossier.employee.job_title || "—"}</Descriptions.Item>
              <Descriptions.Item label="دوره">{dossier.period.label}</Descriptions.Item>
              <Descriptions.Item label="واحد">ریال</Descriptions.Item>
            </Descriptions>

            {/* چرا این عدد؟ — پیش از بقیه، چون اولین سؤالِ کاربر همین است */}
            <PayrollExplainBody data={explainData} />

            <Card size="small" title="مولفه‌های حقوقی" style={{ marginBottom: 12 }}>
              {(dossier.pay_components || []).length === 0
                ? <Text type="secondary">مولفه‌ای ثبت نشده</Text>
                : dossier.pay_components.map((c) => (
                  <Tag key={c.id} color="blue" style={{ marginBottom: 4 }}>{c.component_type}</Tag>
                ))}
            </Card>

            {/* فیشِ افقی (خواستهٔ کاربر): پرداختی‌ها و کسورات کنارِ هم مثل
                صورتحسابِ کاغذی؛ هر ردیف تعداد/ساعت و مبلغِ نهایی دارد و
                نوارِ «خالص پرداختی» جدا پایین می‌آید — منفی قرمز. */}
            <Card size="small" title="فیش (ریال)" style={{ marginBottom: 12 }}>
              {(() => {
                const pn = dossier.payslip_panels;
                if (!pn) return <Text type="secondary">جزئیات فیش موجود نیست</Text>;
                const cols = [
                  { title: "شرح", dataIndex: "label",
                    render: (v, r) => <>{v}{r.note
                      ? <Text type="secondary" style={{ fontSize: 11 }}> ({r.note})</Text> : null}</> },
                  { title: "تعداد", key: "qty", align: "center", width: 110,
                    render: (_, r) => (r.qty ? `${faNum(r.qty)} ${r.unit || ""}` : "—") },
                  // فی (v2.121.0) — هر عملیات/برش یک ردیفِ پرداختی است، مثلِ فیشِ چاپی
                  { title: "فی", dataIndex: "rate", align: "center", width: 100,
                    render: (v) => (v ? faNum(Number(v)) : "—") },
                  { title: "مبلغ", dataIndex: "amount", align: "left", width: 120,
                    render: (v) => faNum(Number(v)) },
                ];
                const panel = (title, rows, totalLabel, total, color) => (
                  <Card size="small" title={title} style={{ flex: "1 1 300px" }}>
                    <Table size="small" pagination={false} rowKey={(r, i) => `${r.label}-${i}`}
                      dataSource={rows} columns={cols}
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}><b>{totalLabel}</b></Table.Summary.Cell>
                          <Table.Summary.Cell index={1} />
                          <Table.Summary.Cell index={2} />
                          <Table.Summary.Cell index={3} align="left">
                            <b style={{ color }}>{faNum(total)}</b>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )} />
                  </Card>
                );
                const neg = Number(pn.net) < 0;
                return (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {panel("پرداختی‌ها (بستانکاری)", pn.earnings, "جمع پرداختی",
                        pn.earn_total, "#107C41")}
                      {panel("کسورات (بدهکاری)", pn.deductions, "جمع کسورات",
                        pn.deduct_total, "#C0392B")}
                    </div>
                    <div style={{
                      marginTop: 10, padding: "10px 14px", borderRadius: 8,
                      background: neg ? "#C0392B" : "#107C41", color: "#fff",
                      display: "flex", justifyContent: "space-between",
                      fontWeight: 700, fontSize: 15,
                    }}>
                      <span>خالص پرداختی</span>
                      <span>{faNum(pn.net)} ریال</span>
                    </div>
                    {!!pn.carried && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        مانده بدهیِ انتقالی به ماه بعد: {faNum(pn.carried)} ریال
                      </Text>
                    )}
                  </>
                );
              })()}
            </Card>

            <Card size="small" title="بازبینی" style={{ marginBottom: 12 }}>
              <Space direction="vertical">
                <Checkbox checked={!!dossier.review.attendance_approved}
                  onChange={async (e) => {
                    await patchEmployeeReview(selectedPeriod, dossier.employee.id, { attendance_approved: e.target.checked });
                    openDossier({ employee_id: dossier.employee.id });
                  }}>تأیید کارکرد حضور</Checkbox>
                <Checkbox checked={!!dossier.review.calculation_approved}
                  onChange={async (e) => {
                    await patchEmployeeReview(selectedPeriod, dossier.employee.id, { calculation_approved: e.target.checked });
                    openDossier({ employee_id: dossier.employee.id });
                  }}>تأیید محاسبه</Checkbox>
                <Checkbox checked={!!dossier.review.payslip_sent}
                  onChange={async (e) => {
                    await patchEmployeeReview(selectedPeriod, dossier.employee.id, { payslip_sent: e.target.checked });
                    openDossier({ employee_id: dossier.employee.id });
                  }}>فیش ارسال شد</Checkbox>
              </Space>
            </Card>

            {dossier.cardex_summary && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message={`غیبت: ${dossier.cardex_summary.absent ?? "—"} | ناقص: ${dossier.cardex_summary.incomplete ?? "—"} | کسری دقیقه: ${faNum(dossier.cardex_summary.total_shortage_minutes || 0)}`}
              />
            )}
            <Text type="secondary">جمع تولید ماه: {faNum(dossier.production_total || 0)} ریال — {dossier.production_lines?.length || 0} ردیف</Text>
          </>
        )}
      </Drawer>

      <RecordEditorModal
        open={editRec.open}
        onClose={() => setEditRec({ open: false, index: 0 })}
        items={filteredRecords}
        initialIndex={editRec.index}
        form={recForm}
        title="ویرایش رکورد حقوق"
        width={1000}
        warning="برای موردِ جامانده یا مبلغِ عوض‌شده، «ردیف اصلاحی» اضافه کنید — فقط روی حقوقِ همین ماه اثر دارد و با محاسبهٔ مجدد نمی‌پرد. فیلدهای پایین، اصلاحِ مستقیمِ مبالغ‌اند و رکورد را «دستی» (قفل در برابرِ محاسبهٔ مجدد) می‌کنند. اگر خودِ کارکرد اشتباه است، در «تولید» یا «حضور و غیاب» اصلاحش کنید. دورهٔ تاییدشده/قفل کد مدیریتی می‌خواهد."
        headerTitle={(r) => r.employee_name || `ID:${r.employee_id}`}
        headerSubtitle={(r) => `کد ${r.employee_code || "—"} • روزکارکرد ${fmt(r.worked_days)}`}
        headerTag={(r) => <Tag color={r.pay_model === "piece" ? "geekblue" : "green"}>{PAY_MODEL_LABEL[r.pay_model] || "—"}</Tag>}
        loadValues={(r) => ({
          gross_pay: Number(r.gross_pay || 0),
          insurance_deduction: Number(r.insurance_deduction || 0),
          advance_deduction: Number(r.advance_deduction || 0),
          buffet_deduction: Number(r.buffet_deduction || 0),
          manual_deduction: Number(r.manual_deduction || 0),
          bonus: Number(r.bonus || 0),
        })}
        renderFields={(r) => (
          <>
            {/* زمینهٔ همین نفر — فقط‌خواندنی، تا بدونِ بستنِ پنجره معلوم باشد
                این اعداد از کجا آمده‌اند. */}
            <RecordContextPanel explain={ctx.explain} dossier={ctx.dossier}
              loading={ctx.loading || ctx.empId !== r.employee_id}
              onCorrect={(row) => setAdjRows([...adj.rows, newAdjRow({
                kind: "piece", title: row.operation_name, unit_price: row.unit_price,
              })])} />
            {/* ردیف‌های اصلاحیِ همین ماه — جدا از فیلدهای فرم، با ذخیرهٔ همان دکمه */}
            <PayrollAdjustmentsEditor
              rows={adj.empId === r.employee_id ? adj.rows : []}
              onChange={setAdjRows} catalog={catalog}
              rateOptions={ctx.empId === r.employee_id ? ctx.dossier?.rate_options : null}
              disabled={ctx.loading || adj.empId !== r.employee_id} />
            {/* حضور و غیابِ همین نفر — دیدن و اصلاح همین‌جا (v2.112.0): ورود/خروج، انتخاب از ترددهای خام،
                «بخشیده — بدونِ کسر»، مأموریت. هر تغییر ⇒ حقوقِ این نفر دوباره حساب می‌شود. */}
            {/* فقط برای کسی که حضورش روی حقوق اثر دارد: حضوریِ دستگاه، یا بازهٔ روزمزد/ساعتی دارد (v2.112.5).
                برای کارمزدیِ خالص/غیرحضوری نمی‌آید تا «غایب ۲۷» به اشتباه نیندازد. */}
            {selectedPeriodObj && ctx.empId === r.employee_id && (
              ctx.explain?.employee?.attendance_required
              || (ctx.dossier?.pay_plan?.ranges || []).some((x) => ["daily", "hourly"].includes(x.rate_type))
            ) && (
              <Collapse size="small" style={{ marginBottom: 12 }} destroyInactivePanel
                items={[{
                  key: "att",
                  label: <span><b>حضور و غیابِ این ماه</b> — دیدن و اصلاح (بخشیدن، ورود/خروج، ترددها)</span>,
                  children: (
                    <PersonDaysPanel employeeId={r.employee_id} employeeName={r.employee_name}
                      year={selectedPeriodObj.year} month={selectedPeriodObj.month}
                      locked={["locked", "accountant_approved", "management_approved"].includes(selectedPeriodObj.closing_status)}
                      onChanged={() => recalcAfterAttendance(r)} />
                  ),
                }]} />
            )}
            {/* انتقالِ بخشی از کسورات به ماهِ بعد — ذخیرهٔ فوری، جدا از دکمهٔ ذخیرهٔ فرم */}
            <DebtDeferralPanel periodId={selectedPeriod} employeeId={r.employee_id}
              runGuarded={runGuardedEdit} onChanged={() => loadCtx(r.employee_id)} />
            <Row gutter={12}>
              <Col xs={24} sm={12}><Form.Item name="gross_pay" label="حقوق ناخالص (ریال)"><MoneyInput /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="insurance_deduction" label="بیمه (ریال)"><MoneyInput /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="advance_deduction" label="مساعده (ریال)"><MoneyInput /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="buffet_deduction" label="بوفه (ریال)"><MoneyInput /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="manual_deduction" label="جریمه (ریال)"><MoneyInput /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="bonus" label="پاداش (ریال)"><MoneyInput /></Form.Item></Col>
            </Row>
          </>
        )}
        onItemChange={(r) => loadCtx(r?.employee_id)}
        renderPreview={(v, r) => {
          const partial = (o) => Number(o.gross_pay || 0) - Number(o.insurance_deduction || 0)
            - Number(o.advance_deduction || 0) - Number(o.buffet_deduction || 0)
            - Number(o.manual_deduction || 0) + Number(o.bonus || 0);
          // اثرِ ردیف‌های اصلاحیِ ذخیره‌نشده هم در برآورد می‌آید
          const t = adj.empId === r.employee_id ? adjTotals(adj.rows) : adj.saved;
          const dAdj = (t.earn - adj.saved.earn) - (t.deduct - adj.saved.deduct);
          const est = Number(r.net_pay || 0) + partial(v) - partial(r) + dAdj;
          return (
            <Space split="•" wrap>
              <span>حقوق خالصِ فعلی: <b>{fmt(r.net_pay)}</b></span>
              {dAdj !== 0 && <span>ردیف‌های اصلاحی: <b style={{ color: dAdj < 0 ? "#C0392B" : "#107C41" }}>{dAdj > 0 ? "+" : ""}{fmt(dAdj)}</b></span>}
              <span>برآوردِ پس از تغییر: <b style={{ color: est < 0 ? "#C0392B" : "#107C41" }}>{fmt(est)}</b></span>
            </Space>
          );
        }}
        isExtraDirty={() => adj.dirty}
        saveExtra={saveAdjustments}
        onSave={(item, changed) => runGuardedEdit(
          (pin) => updatePayrollRecord(selectedPeriod, item.id, changed, pin),
          "رکورد حقوق ذخیره شد")}
      />
      {pdf.node}
    </div>
  );
}
